import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import type { TopicDef } from "@/lib/topics-config";
import { TOPICS } from "@/lib/topics-config";
import type { TopicIndexData, TopicEntry } from "@/lib/topics.server";
import { topicKeyStats, topicFaqs } from "@/lib/topic-stats";
import { FaqBlock } from "@/components/FaqBlock";
import { topicNarrative } from "@/lib/topic-narrative";
import { TablePager } from "@/components/TablePager";
import { paginate, type SortDir } from "@/lib/table-state";


function fmt(value: number | null | undefined, decimals: number) {
  if (value == null) return "—";
  return value.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

export function topicAnswer(def: TopicDef, data: TopicIndexData): string {
  const top = data.entries[0];
  if (!top) return def.description;
  const second = data.entries[1];
  const lead = `The ${top.makeName} ${top.modelName} tops the list at ${fmt(top.value, def.decimals)} ${def.unitLong}`;
  const runner = second
    ? `, with the ${second.makeName} ${second.modelName} next at ${fmt(second.value, def.decimals)}`
    : "";
  return `${lead}${runner}. This chart covers ${data.modelCount} models with published ${def.metricLabel.toLowerCase()} figures.`;
}

// Column visibility is a data attribute read by the `rank-table` utility, so a
// 1,000-row table does not repeat responsive class strings on every cell.

type SortKey = "rank" | "value" | "year" | string;

// Only the top slice is server-rendered. Full tables ran to ~1,500 rows and
// pushed the HTML payload past 700KB, which Semrush/PSI flag as slow load
// speed. The rest is one click away and every model stays reachable from its
// brand and model hub.
const VISIBLE_ROWS = 150;

/** Cross-brand ranked table for one spec type — the topical parent of every leaf spec page. */
export function TopicIndex({ def, data }: { def: TopicDef; data: TopicIndexData }) {
  const answer = topicAnswer(def, data);
  const others = TOPICS.filter((t) => t.slug !== def.slug);
  const extras = def.extraColumns ?? [];
  const keyStats = topicKeyStats(def, data);
  const faqs = topicFaqs(def, data, answer);

  // Sort + page live in the URL, so a sorted or deep page is shareable and the
  // server renders exactly what was requested.
  const urlState = useSearch({ strict: false }) as {
    sort?: string;
    dir?: SortDir;
    page?: number;
  };
  const navigate = useNavigate();
  const sort: { key: SortKey; dir: SortDir } = {
    key: urlState.sort || "rank",
    dir: urlState.dir || (urlState.sort && urlState.sort !== "rank" ? "desc" : "asc"),
  };

  function valueFor(e: TopicEntry, key: SortKey): number {
    if (key === "rank") return e.rank;
    if (key === "value") return e.value;
    if (key === "year") return e.year;
    return e.extra?.[key] ?? Number.NEGATIVE_INFINITY;
  }

  const rows =
    sort.key === "rank" && sort.dir === "asc"
      ? data.entries
      : [...data.entries].sort((a, b) => {
          const diff = valueFor(a, sort.key) - valueFor(b, sort.key);
          return sort.dir === "asc" ? diff : -diff;
        });

  function toggle(key: SortKey) {
    const nextDir: SortDir =
      sort.key === key ? (sort.dir === "asc" ? "desc" : "asc") : key === "rank" ? "asc" : "desc";
    const defaultDir: SortDir = key === "rank" ? "asc" : "desc";
    navigate({
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      search: ((prev: any) => ({
        ...prev,
        sort: key === "rank" ? undefined : key,
        dir: nextDir === defaultDir ? undefined : nextDir,
        page: undefined,
      })) as any,
    });
  }

  const narrative = topicNarrative(def, data);
  const paged = paginate(rows, urlState.page ?? 1, VISIBLE_ROWS);
  const visibleRows = paged.rows;
  const hiddenCount = rows.length - VISIBLE_ROWS > 0 ? rows.length - VISIBLE_ROWS : 0;


  function SortButton({ label, sortKey }: { label: string; sortKey: SortKey }) {
    const active = sort.key === sortKey;
    return (
      <button
        type="button"
        onClick={() => toggle(sortKey)}
        aria-label={`Sort by ${label}`}
        className="inline-flex items-center gap-1 font-semibold text-foreground hover:text-primary"
      >
        {label}
        <span aria-hidden="true" className={active ? "opacity-100" : "opacity-30"}>
          {active && sort.dir === "asc" ? "▲" : "▼"}
        </span>
      </button>
    );
  }

  return (
    <div>
      <nav className="mx-auto max-w-5xl px-4 sm:px-6 pt-4 text-sm text-muted-foreground">
        <Link to="/" className="hover:text-foreground">
          Home
        </Link>
        <span className="mx-2">/</span>
        <span className="text-foreground">{def.h1}</span>
      </nav>

      <section className="border-b border-border bg-gradient-to-b from-secondary/40 to-background">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 py-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground">{def.h1}</h1>
          <aside
            data-aeo="atomic-passage"
            aria-label="Quick answer summary"
            className="mt-4 max-w-3xl rounded-xl border border-border/80 bg-muted/40 p-4 shadow-sm"
          >
            <p className="text-base leading-relaxed text-foreground">{answer}</p>
          </aside>
          <p className="mt-3 max-w-3xl text-sm text-muted-foreground">{def.intro}</p>
        </div>
      </section>

      {narrative.length > 0 ? (
        <section className="mx-auto max-w-5xl px-4 sm:px-6 pt-8" aria-labelledby="chart-analysis">
          <h2 id="chart-analysis" className="text-lg font-semibold text-foreground">
            What this {def.metricLabel.toLowerCase()} ranking shows
          </h2>
          {narrative.map((p, i) => (
            <p key={i} className="mt-3 max-w-3xl text-sm leading-relaxed text-muted-foreground">
              {p}
            </p>
          ))}
        </section>
      ) : null}

      {keyStats.length > 0 ? (
        <section
          className="mx-auto max-w-5xl px-4 sm:px-6 pt-8"
          aria-labelledby="key-statistics"
          data-aeo="key-statistics"
        >
          <h2 id="key-statistics" className="text-lg font-semibold text-foreground">
            Key statistics
          </h2>
          <dl className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {keyStats.map((s) => (
              <div key={s.label} className="rounded-lg border border-border bg-card p-4">
                <dt className="text-xs uppercase tracking-wide text-muted-foreground">{s.label}</dt>
                <dd className="mt-1 text-xl font-semibold text-foreground">{s.value}</dd>
                <dd className="mt-1 text-xs text-muted-foreground">{s.detail}</dd>
              </div>
            ))}
          </dl>
          <p className="mt-3 text-xs text-muted-foreground">
            Source: AutoSpecData analysis of {data.modelCount.toLocaleString("en-US")} models,
            current through model year {data.latestYear}.
          </p>
        </section>
      ) : null}

      <section className="mx-auto max-w-5xl px-4 sm:px-6 py-8">
        <p className="mb-3 text-xs text-muted-foreground">
          Tap any column heading to re-sort the table.
        </p>
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="rank-table">
            <caption className="sr-only">
              {def.h1} — {data.modelCount} models ranked by {def.metricLabel.toLowerCase()}
            </caption>
            <thead className="bg-secondary/60">
              <tr>
                <th scope="col">
                  <SortButton label="#" sortKey="rank" />
                </th>
                <th scope="col">Vehicle</th>
                <th scope="col">
                  <SortButton label={`${def.metricLabel} (${def.unit})`} sortKey="value" />
                </th>
                {extras.map((c) => (
                  <th key={c.key} scope="col" data-hide={c.hideBelow || undefined}>
                    <SortButton label={c.label} sortKey={c.key} />
                  </th>
                ))}
                <th scope="col" data-hide="lg">
                  Configuration
                </th>
                <th scope="col" data-hide="md">
                  <SortButton label="Year" sortKey="year" />
                </th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((e) => (
                <tr key={`${e.makeSlug}-${e.modelSlug}`}>
                  <td>{e.rank}</td>
                  <td>
                    <Link
                      to="/$make/$model/$dataType"
                      params={{
                        make: e.makeSlug,
                        model: e.modelSlug,
                        dataType: def.specType,
                      }}
                      className="font-medium text-foreground hover:text-primary"
                    >
                      {e.makeName} {e.modelName}
                    </Link>
                  </td>
                  <td data-strong="">{fmt(e.value, def.decimals)}</td>
                  {extras.map((c) => (
                    <td key={c.key} data-hide={c.hideBelow || undefined}>
                      {fmt(e.extra?.[c.key] ?? null, c.decimals)}
                    </td>
                  ))}
                  <td data-hide="lg">{e.config ?? "—"}</td>
                  <td data-hide="md">{e.year}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <TablePager
          page={paged.page}
          totalPages={paged.totalPages}
          label={`${def.h1} table pages`}
        />

        <p className="mt-4 text-center text-xs text-muted-foreground">
          Showing rows {(paged.start + 1).toLocaleString("en-US")}–
          {(paged.start + visibleRows.length).toLocaleString("en-US")} of{" "}
          {paged.total.toLocaleString("en-US")}.
        </p>

        <div className="mt-4 text-center">
          <Link
            to="/search"
            search={{ q: "", make: "", model: "", year: undefined, type: "", page: 1 }}
            className="cta-gold rounded-full"
          >
            Find your vehicle
          </Link>
        </div>

        <p className="mt-4 max-w-3xl text-sm text-muted-foreground">{def.notes}</p>
      </section>

      {hiddenCount > 0 && paged.page === 1 ? (
        <section className="mx-auto max-w-5xl px-4 sm:px-6 pb-8" aria-labelledby="all-models">
          <h2 id="all-models" className="text-lg font-semibold text-foreground">
            Every other model in this dataset
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {hiddenCount.toLocaleString("en-US")} additional models ranked below the top{" "}
            {VISIBLE_ROWS} — open any one for its full {def.metricLabel.toLowerCase()} breakdown.
          </p>
          <ul className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-sm">
            {rows.slice(VISIBLE_ROWS).map((e) => (
              <li key={`link-${e.makeSlug}-${e.modelSlug}`}>
                <Link
                  to="/$make/$model/$dataType"
                  params={{ make: e.makeSlug, model: e.modelSlug, dataType: def.specType }}
                  className="text-muted-foreground hover:text-primary"
                >
                  {e.makeName} {e.modelName}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ) : null}



      {faqs.length > 0 ? (
        <section className="mx-auto max-w-5xl px-4 sm:px-6 pb-10 border-t border-border pt-8">
          <h2 className="text-lg font-semibold text-foreground mb-4">
            Frequently asked questions
          </h2>
          <FaqBlock items={faqs} />
        </section>
      ) : null}

      <section className="mx-auto max-w-5xl px-4 sm:px-6 pb-12 border-t border-border pt-8">
        <h2 className="text-lg font-semibold text-foreground mb-3">Other spec charts</h2>
        <ul className="flex flex-wrap gap-2">
          {others.map((t) => (
            <li key={t.slug}>
              <Link
                to={t.path}
                className="chip-link"
              >
                {t.h1}
              </Link>
            </li>
          ))}
          <li>
            <Link
              to="/maintenance-comparison"
              className="chip-link"
            >
              Maintenance intervals by brand
            </Link>
          </li>
          <li>
            <Link
              to="/best"
              className="chip-link"
            >
              Fuel economy rankings
            </Link>
          </li>
          {def.slug === "towing-capacity" ? (
            <li>
              <Link to="/how-much-can-my-car-tow" className="chip-link">
                How much can my car tow — lookup tool
              </Link>
            </li>
          ) : null}
        </ul>
      </section>
    </div>
  );
}
